/**
 * Following someone else's journey.
 *
 * The rider's phone already tells the server where he is in his morning; this turns that record
 * into a message for whoever is meeting him. Nothing new is collected — the follower sees the
 * phases the rider's own app is already reporting, and the train he has already chosen.
 *
 * Deliberately verbose while this is being tried out: every phase change is worth a line, and
 * the train times are the whole point of the exercise.
 */

import { isBefore, RIDER_PHASE_LABELS } from "./rider-journey";
import { BART_STATIONS, COLLEGE_PREP_BOUNDARY, profileById } from "./profiles";
import { clearlyInsideBoundary, clearlyOutsideBoundary } from "./journey";
import { FOLLOWER_HOME_FRESH_MS, homeEvidence, readHomeFix } from "./home-arrival";
import { headsignLabel, stationLabel } from "../shared/labels";
import { defaultOriginFor, homeForStation } from "../shared/origins";
import { distanceMeters, formatClockWithMeridiem, serviceDateKey } from "../shared/time";
import type { PickupArrivalConfidence, PickupState, RiderPhase } from "../shared/types";
import { LAST_KNOWN_FIX_MS, mapFix, type MapFix } from "./pickup-map";
import { trainProgressForStops } from "./progress";
import { ARRIVING_SECONDS } from "./ride";
import { BART_YELLOW_STATIONS as YELLOW_LINE_STATIONS, bartTrackPosition } from "./bart-geometry";

/** One stop on the followed train, as the rider's own screen has it. */
export interface FollowStop {
	stopId: string;
	stopName: string;
	predictedMs: number;
}

/** What one diagnostic row says about where the rider is. */
export interface FollowReading {
	phase: RiderPhase;
	/** The leg he is on: the app's own word, e.g. "school" or "home". */
	direction: string | null;
	route: string | null;
	headsign: string | null;
	boardingStationId?: string | null;
	boardingName: string | null;
	boardingMs: number | null;
	alighting: FollowStop | null;
	/** Where the journey really ends, past the last platform. Only the school run has one. */
	finalLabel: string | null;
	finalMs: number | null;
	/** He has left the place this leg starts from — on the ride home, school. */
	leftStart: boolean;
	/** Where the phone was when it said this, when the row carries a fix at all. */
	position: { lat: number; lon: number; accuracyMeters: number } | null;
	/** Timed stations used only to draw an explicitly inferred train position. */
	trainStops?: readonly FollowStop[];
	arrivalContradicted?: boolean;
}

export type FollowEvidence = "declared" | "observed" | "inferred";

/** The latest rider facts worth keeping visible on the driver's screen. */
export interface DriverFollowStatus extends FollowReading {
	lastKnownPosition?: MapFix | null;
	driverLastKnownPosition?: MapFix | null;
	trainObservation?: { stationName: string; atMs: number };
	evidence: FollowEvidence | null;
	updatedAtMs: number;
	atShuttleStop: boolean;
	planId: string | null;
	homeName: string | null;
	stationArrivalMs: number | null;
	nextStopName: string | null;
	nextStopId: string | null;
	arriving: boolean;
	stopsRemaining: number | null;
	destinationName: string | null;
	etaMs: number | null;
	timingStatus?: "unavailable" | "overdue";
	passedStopId?: string;
}

export function driverHomeObservation(status: DriverFollowStatus, nowMs: number): { name: string; atMs: number } | null {
	if (status.phase !== "planning" && status.phase !== "at_home" && status.phase !== "arrived") return null;
	const position = mapFix(status.lastKnownPosition, nowMs, LAST_KNOWN_FIX_MS);
	if (!position || position.source === "manual-test" || position.fixAtMs > nowMs ||
		position.fixAtMs < status.updatedAtMs - FOLLOWER_HOME_FRESH_MS ||
		(status.evidence === "declared" && position.fixAtMs < status.updatedAtMs)) return null;
	const evidence = homeEvidence(position);
	return evidence.kind === "inside" ? { name: evidence.home.onwardLabel ?? evidence.home.label, atMs: position.fixAtMs } : null;
}

/** What the follower was last told, so a reopened app is not an event. */
export interface FollowMark {
	date: string | null;
	direction: string | null;
	step: string | null;
	/** When that step was sent, which is what makes a reversal readable as a correction. */
	sentAtMs: number | null;
}

const PHASES = new Set<string>(Object.keys(RIDER_PHASE_LABELS));

/**
 * One step of the journey as the follower experiences it, which is not one phase.
 *
 * The two legs are watched from opposite ends, so they are not the same story told backwards.
 * Going out he is dropped at the station, so the first thing worth saying is that he is through
 * the faregates; coming home nobody saw him leave school, so opening the app is itself news.
 * `waiting` is one step from two phases: at the station and on the platform are ninety seconds
 * apart and mean the same thing to someone at the other end, so whichever lands first is it.
 */
export type FollowStep = "start" | "leaving" | "waiting" | "train" | "off" | "arrived";

/** The order a morning happens in, which is what makes a step backwards recognisable. */
const FOLLOW_STEPS: FollowStep[] = ["start", "leaving", "waiting", "train", "off", "arrived"];

const OUTBOUND: Partial<Record<RiderPhase, FollowStep>> = {
	on_platform: "waiting",
	on_train: "train",
	at_destination: "off",
	arrived: "arrived",
};

const HOMEWARD: Partial<Record<RiderPhase, FollowStep>> = {
	at_home: "start",
	at_station: "waiting",
	on_platform: "waiting",
	on_train: "train",
	at_destination: "off",
};

/**
 * The step this phase is, or null when it is not one the follower is told about.
 *
 * Leaving school has no phase of its own — the shuttle is five minutes and the rider has nothing
 * to do about it — but it is the one moment on the ride home that someone meeting him cares
 * about, because it is the first sign he is on his way at all.
 */
export function followStep(phase: RiderPhase, direction: string | null, leftStart = false): FollowStep | null {
	if (direction === "home" && leftStart && phase === "at_home") {
		return "leaving";
	}
	return (direction === "home" ? HOMEWARD : OUTBOUND)[phase] ?? null;
}

/** The step this reading is, or null when it is not one the follower is told about. */
export function stepOf(reading: FollowReading): FollowStep | null {
	return followStep(reading.phase, reading.direction, reading.leftStart);
}

/**
 * Whether this ladder should say the step at all.
 *
 * Two independent ladders run at once on the ride home and the follower is also the driver, so on
 * 2026-09-09 he was buzzed twice in the same second, twice: "Eben is at school · He has the app
 * open." beside "Eben is heading home · He has the app open at College Prep.", and "Eben has left
 * school" beside "Eben is heading home · Arriving Civic Center at 4:03 pm."
 *
 * The pickup half wins both because it says strictly more — the station and the arrival — and it
 * opens Pickup Mode when tapped, where these two open the app. The later steps are not a clash:
 * `waiting` has no pickup rung (`tentative` is deliberately silent) and `pinned` fires on the
 * platform, minutes before `train`.
 *
 * `atKnownHome` is the second gate and it is about truth rather than duplication. Coming home,
 * `at_home` is the leg's *default* phase and not a sighting — it is what the app says before
 * anything has happened. So opening the app on the sofa in the evening announced "Eben is at
 * school", on 2026-09-05 and again at 19:57 on 2026-09-09 with a fix twenty-five metres from the
 * house on the same row. Standing inside a known house is the one fact that positively contradicts
 * the claim. Being *outside* every house proves nothing either way, and deliberately does not
 * license it.
 *
 * `awayFromSchool` is the same rule pointed at the other leg. Going out, `arrived` says "he is at
 * school" too, and had no gate of any kind until 2026-09-16, when a phone that woke in the car
 * still holding the previous morning's `arrived` said it at 07:24. That row carried no position,
 * so this would not have caught it — the day rollover in `useJourneyProgress` is what does. This
 * covers the same claim arriving *with* a fix that refutes it. A fix whose whole circle is off
 * campus does refute it; a row with no usable fix does not, because reaching school with the phone
 * in a pocket is the ordinary way that step arrives.
 */
export function followAnnouncesStep(
	step: FollowStep,
	direction: string | null,
	evidence: { pickupSpeaks: boolean; atKnownHome: boolean; atSchool?: boolean; awayFromSchool?: boolean },
): boolean {
	if (direction !== "home") {
		return step !== "arrived" || evidence.awayFromSchool !== true;
	}
	if (step !== "start" && step !== "leaving") {
		return true;
	}
	return !evidence.pickupSpeaks && !evidence.atKnownHome && (step !== "start" || evidence.atSchool === true);
}

export function followAtSchool(detail: unknown, nowMs: number): boolean {
	const fix = freshFollowFix(detail, nowMs);
	return fix != null && clearlyInsideBoundary(fix, COLLEGE_PREP_BOUNDARY);
}

/** A fresh fix with no part of its circle on campus, which is the only thing that can refute being there. */
export function followAwayFromSchool(detail: unknown, nowMs: number): boolean {
	const fix = freshFollowFix(detail, nowMs);
	return fix != null && clearlyOutsideBoundary(fix, COLLEGE_PREP_BOUNDARY);
}

function freshFollowFix(detail: unknown, nowMs: number): { lat: number; lon: number; accuracyMeters: number } | null {
	const fix = readHomeFix(record(detail)?.location);
	const usable = fix != null && Number.isFinite(fix.lat) && Number.isFinite(fix.lon) &&
		Number.isFinite(fix.accuracyMeters) && fix.accuracyMeters >= 0 &&
		nowMs - fix.fixAtMs >= 0 && nowMs - fix.fixAtMs <= FOLLOWER_HOME_FRESH_MS;
	return usable ? fix : null;
}

/**
 * Whether this reading can say anything the follower can use.
 *
 * Waiting and riding are both claims about a train, and the deck is sometimes a beat behind the
 * phase — so "on a train / No train picked yet." is a buzz that answers nothing. Waiting costs
 * nothing: the mark only moves when something is sent, so the next diagnostic with a plan on it
 * sends the step properly.
 */
export function hasSomethingToSay(reading: FollowReading): boolean {
	const step = stepOf(reading);
	if (step === "arrived" && reading.arrivalContradicted) {
		return false;
	}
	if (step !== "waiting" && step !== "train") {
		return true;
	}
	return reading.route != null || reading.boardingMs != null;
}

function text(value: unknown): string | null {
	return typeof value === "string" && value.length > 0 ? value : null;
}

function number(value: unknown): number | null {
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function record(value: unknown): Record<string, unknown> | null {
	return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
}

function followStops(value: unknown): FollowStop[] {
	if (!Array.isArray(value)) return [];
	const parsed: FollowStop[] = [];
	for (const entry of value) {
		const stop = record(entry);
		const stopId = text(stop?.stopId);
		const stopName = text(stop?.stopName);
		const predictedMs = number(stop?.predictedMs);
		if (stopId && stopName && predictedMs != null) parsed.push({ stopId, stopName, predictedMs });
	}
	return parsed;
}

/**
 * Pull the followable facts out of a diagnostic.
 *
 * Returns null when the row cannot say where he is, which is most of them: connectivity blips
 * and early rows carry no phase, and a phase is the only thing worth telling anyone about.
 */
export function readFollowDiagnostic(detail: unknown): FollowReading | null {
	const body = record(detail);
	const state = record(body?.state);
	const phase = text(state?.phase);
	if (!phase || !PHASES.has(phase)) {
		return null;
	}

	// The train he has settled on, which once he is moving is the pinned one rather than the
	// recommendation. Falling back to the selection keeps a plan on the message before that.
	const plan = record(body?.alertPlan) ?? record(body?.selected);
	const parsed = followStops(plan?.destinations);
	const chosenId = text(body?.destinationStopId);
	const alighting = (chosenId ? parsed.find((stop) => stop.stopId === chosenId) : null) ?? parsed[0] ?? null;
	const location = record(body?.location);
	const lat = number(location?.lat);
	const lon = number(location?.lon);
	const accuracyMeters = number(location?.accuracyMeters);

	return {
		phase: phase as RiderPhase,
		direction: text(state?.direction),
		route: text(plan?.route),
		headsign: text(plan?.headsign),
		boardingStationId: profileById(text(body?.profileId))?.boardingStationId ?? null,
		boardingName: text(body?.boardingStationName),
		boardingMs: number(plan?.boardingMs),
		alighting,
		finalLabel: text(body?.finalLabel),
		finalMs: number(plan?.finalArrivalMs),
		leftStart: state?.leftSchool === true,
		position: lat == null || lon == null || accuracyMeters == null ? null : { lat, lon, accuracyMeters },
		trainStops: followStops(plan?.track),
		arrivalContradicted: state?.phaseSource === "observed" && state?.arrivedAtFinal === false,
	};
}

/** Briefly clamp an overdue estimate at the destination, never leave it there indefinitely. */
const STALE_TRAIN_STOPS_MS = 4 * 60_000;

function journeyStops(status: DriverFollowStatus): FollowStop[] {
	const stops = [...(status.trainStops ?? [])];
	const boardingIndex = stops.findIndex(stop => stop.stopId === status.boardingStationId);
	const destinationIndex = stops.findIndex(stop => stop.stopId === status.alighting?.stopId);
	if (boardingIndex < 0 || destinationIndex < boardingIndex) return [];
	return stops.slice(boardingIndex, destinationIndex + 1);
}

/** One clock-driven estimate for every follower surface; never journey evidence. */
export function projectFollowStatus(status: DriverFollowStatus | null, nowMs: number): DriverFollowStatus | null {
	if (!status || status.phase !== "on_train") return status;
	const stops = journeyStops(status);
	const destination = stops[stops.length - 1];
	const passedIndex = stops.findIndex(stop => stop.stopId === status.passedStopId);
	const nextIndex = stops.findIndex((stop, index) => index > passedIndex && stop.predictedMs > nowMs);
	const next = nextIndex < 0 ? null : stops[nextIndex];
	const etaMs = destination?.predictedMs ?? status.etaMs;
	const overdue = etaMs != null && etaMs <= nowMs;
	if (!stops.length || overdue) {
		return {
			...status,
			nextStopName: null, nextStopId: null, arriving: false, stopsRemaining: null,
			etaMs: overdue || status.timingStatus === "unavailable" ? null : etaMs,
			timingStatus: overdue ? "overdue" : "unavailable",
		};
	}
	return {
		...status,
		nextStopName: next?.stopName ?? null,
		nextStopId: next?.stopId ?? null,
		arriving: next != null && next.predictedMs - nowMs <= ARRIVING_SECONDS * 1000,
		stopsRemaining: nextIndex < 0 ? null : stops.length - nextIndex,
		destinationName: destination.stopName,
		etaMs,
		timingStatus: undefined,
	};
}

export function showMorningFollowMap(status: DriverFollowStatus | null, driverPhase: RiderPhase, driverDirection: string, boardingStationId: string | null): boolean {
	if (!status || status.direction !== "school" || driverDirection !== "school" || status.phase === "arrived") return false;
	return !isBefore(status.phase, "at_station") ||
		(!isBefore(driverPhase, "at_station") && boardingStationId != null && status.boardingStationId === boardingStationId);
}

/**
 * The train's own stops and next station for any map that follows him, either direction.
 *
 * Only while he is on the train: afterwards the last "next stop" is a station he has already
 * passed, and saying it under a map of him walking out of Civic Center would be wrong.
 */
export function followJourneyOverlay(status: DriverFollowStatus | null): { stops: readonly FollowStop[]; nextStopName: string | null } | null {
	if (!status || status.phase !== "on_train") return null;
	return { stops: status.trainStops ?? [], nextStopName: status.nextStopName ? stationLabel(status.nextStopName) : null };
}

/** Display-only platform/train position. It must not feed journey evidence or phase decisions. */
export function estimatedFollowMapPosition(status: DriverFollowStatus, nowMs: number): MapFix | null {
	if (status.phase === "at_station" || status.phase === "on_platform") {
		const station = status.boardingStationId ? bartTrackPosition(status.boardingStationId, status.boardingStationId, 0) : null;
		return station ? { ...station, fixAtMs: nowMs, accuracyMeters: 0, speedMetersPerSecond: null } : null;
	}
	if (status.phase === "at_destination" && status.evidence === "inferred") {
		const station = status.alighting ? bartTrackPosition(status.alighting.stopId, status.alighting.stopId, 0) : null;
		return station ? { ...station, fixAtMs: nowMs, accuracyMeters: 0, speedMetersPerSecond: null } : null;
	}
	if (status.phase !== "on_train") return null;
	const stops = journeyStops(status);
	if (!stops.length || status.timingStatus === "unavailable") return null;
	const observedPosition = mapFix(status.lastKnownPosition, nowMs, LAST_KNOWN_FIX_MS);
	// A sighting is a checkpoint, not a permanent handover — it lets go the moment fixes stop
	// arriving, which is what lets it be trusted again on the far side of a second tunnel (Balboa
	// Park to the SFO tunnel) rather than freezing the dot at whichever checkpoint came first.
	if (status.evidence === "observed" && status.trainObservation && observedPosition &&
		observedPosition.source !== "manual-test" && observedPosition.accuracyMeters <= 50 &&
		observedPosition.fixAtMs >= status.trainObservation.atMs &&
		nowMs - observedPosition.fixAtMs <= FOLLOWER_HOME_FRESH_MS) return null;
	// The feed can revert passed stops to scheduled times. Never reorder the journey to match
	// those timestamps, or interpolate backwards across an observed passage.
	const passedIndex = stops.findIndex(stop => stop.stopId === status.passedStopId);
	const aheadIndex = stops.findIndex((stop, index) => index > passedIndex && stop.predictedMs > nowMs);
	const segment = aheadIndex > 0 ? stops.slice(aheadIndex - 1, aheadIndex + 1) : stops;
	if (segment.some((stop, index) => index > 0 && stop.predictedMs <= segment[index - 1].predictedMs)) return null;
	const progress = trainProgressForStops(segment, nowMs);
	if (!progress || progress.beyondKnownTrack) return null;
	const lastKnownStopMs = stops[stops.length - 1]?.predictedMs;
	if (lastKnownStopMs != null && nowMs - lastKnownStopMs > STALE_TRAIN_STOPS_MS) return null;
	const position = bartTrackPosition(progress.fromStopId, progress.toStopId, progress.fraction);
	if (!position) return null;
	return {
		...position,
		fixAtMs: nowMs,
		accuracyMeters: 0,
		speedMetersPerSecond: null,
	};
}

/** Turn the rider's latest diagnostic into the driver's persistent status readout. */
export function driverFollowStatus(detail: unknown, updatedAtMs: number): DriverFollowStatus | null {
	const reading = readFollowDiagnostic(detail);
	const body = record(detail);
	const state = record(body?.state);
	const plan = record(body?.alertPlan) ?? record(body?.selected);
	if (!reading || !state) {
		return null;
	}
	const source = text(state.phaseSource);
	const home = defaultOriginFor(text(body?.profileId) ?? "") ?? homeForStation(reading.alighting?.stopId ?? null);
	// Off the train underground is the timetable talking, not a sighting. Announcing it as observed
	// told the driver on 2026-09-15 that Rockridge had been seen while the phone was at West Oakland.
	const guessedOff = reading.phase === "at_destination" && text(state.atDestinationEvidence) === "timetable";
	const evidence: FollowEvidence | null =
		source === "declared"
			? "declared"
			: source === "observed"
				? reading.phase === "on_train" || guessedOff
					? "inferred"
					: "observed"
				: null;

	return {
		...reading,
		evidence,
		updatedAtMs,
		atShuttleStop: false,
		planId: text(plan?.id),
		homeName: home?.onwardLabel ?? home?.label ?? null,
		stationArrivalMs: number(plan?.stationArrivalMs),
		nextStopName: text(state.rideNext),
		nextStopId: text(state.rideNextId),
		arriving: state.rideArriving === true,
		stopsRemaining: number(state.rideStopsToGo),
		destinationName: text(state.rideDestination),
		etaMs: number(state.rideEtaMs) ?? reading.alighting?.predictedMs ?? null,
	};
}

/** Replace all train facts together, or explicitly discard timings from the previous train. */
export function reconcileDriverTrainStatus(
	status: DriverFollowStatus,
	detail: unknown,
	kind: "selection" | "passage",
	recordedAtMs: number,
	nowMs: number,
	timezone = "America/Los_Angeles",
	statusOrderedAtMs = status.updatedAtMs,
): DriverFollowStatus {
	const body = record(detail);
	if (!body || recordedAtMs > nowMs || recordedAtMs < statusOrderedAtMs ||
		serviceDateKey(recordedAtMs, timezone) !== serviceDateKey(nowMs, timezone) ||
		serviceDateKey(status.updatedAtMs, timezone) !== serviceDateKey(nowMs, timezone) ||
		(text(body.direction) != null && body.direction !== status.direction) ||
		!isBefore(status.phase, "at_destination")) return status;
	const planId = text(body.planId);
	if (planId && planId !== status.planId && planId.split("@")[1] !== serviceDateKey(nowMs, timezone).replace(/-/g, "")) return status;
	const phase = text(body.phase);
	if (kind === "passage" && (!phase || !PHASES.has(phase) || !planId || phase === "planning")) return status;
	const plan = record(body.plan);
	const parsed = plan && text(plan.id) === planId
		? driverFollowStatus({
			profileId: body.profileId,
			boardingStationName: body.boardingStationName,
			destinationStopId: body.destinationStopId,
			finalLabel: status.finalLabel,
			alertPlan: plan,
			state: { phase: status.phase, direction: status.direction },
		}, status.updatedAtMs)
		: null;
	let next = status;
	if (parsed) {
		next = {
			...status,
			planId, route: parsed.route, headsign: parsed.headsign,
			boardingStationId: parsed.boardingStationId ?? status.boardingStationId,
			boardingName: parsed.boardingName ?? status.boardingName,
			boardingMs: parsed.boardingMs, stationArrivalMs: parsed.stationArrivalMs,
			alighting: parsed.alighting, trainStops: parsed.trainStops,
			finalMs: parsed.finalMs, destinationName: parsed.alighting?.stopName ?? null,
			etaMs: parsed.etaMs, timingStatus: undefined,
			nextStopId: null, nextStopName: null, arriving: false, stopsRemaining: null,
			...(planId !== status.planId ? { trainObservation: undefined, passedStopId: undefined, evidence: status.phase === "on_train" ? "inferred" as const : status.evidence } : {}),
		};
	} else if (planId !== status.planId) {
		next = {
			...status, planId, route: null, headsign: null, boardingMs: null, stationArrivalMs: null,
			alighting: null, trainStops: [], finalMs: null, etaMs: null, timingStatus: "unavailable",
			nextStopId: null, nextStopName: null, arriving: false, stopsRemaining: null,
			trainObservation: undefined, passedStopId: undefined,
			evidence: status.phase === "on_train" ? "inferred" : status.evidence,
		};
	}
	if (kind === "passage" && phase && isBefore(next.phase, phase as RiderPhase)) {
		next = { ...next, phase: phase as RiderPhase, evidence: phase === "on_train" ? "inferred" : next.evidence };
	}
	const station = text(body.station);
	return kind === "passage" && station ? advanceDriverTrainStatus(next, station) : next;
}

export function followMapPosition(status: DriverFollowStatus | null, nowMs: number): MapFix | null {
	if (!status) return null;
	const estimated = estimatedFollowMapPosition(status, nowMs);
	if (estimated) return estimated;
	const position = mapFix(status.lastKnownPosition, nowMs, LAST_KNOWN_FIX_MS);
	if (status.phase === "on_train" && position &&
		(position.accuracyMeters > 50 || nowMs - position.fixAtMs > FOLLOWER_HOME_FRESH_MS)) return null;
	return position;
}

/**
 * Repair a stale follower readout from the rider's newer destination latch.
 *
 * Notifications consume every diagnostic, while the readout starts from heartbeat/state-change
 * rows. A backgrounded rider can therefore announce the destination before another full state row
 * is posted. Direction and plan guards keep a late diagnostic from another journey from ending
 * the train currently shown.
 */
export function advanceDriverDestinationStatus(status: DriverFollowStatus, detail: unknown, recordedAtMs: number): DriverFollowStatus {
	const latch = record(detail);
	const evidence = text(latch?.evidence);
	if (status.phase !== "on_train" ||
		status.direction == null ||
		status.planId == null ||
		(evidence !== "fence" && evidence !== "timetable") ||
		text(latch?.direction) !== status.direction ||
		text(latch?.pinnedId) !== status.planId) {
		return status;
	}
	return {
		...status,
		phase: "at_destination",
		evidence: evidence === "fence" ? "observed" : "inferred",
		updatedAtMs: Math.max(status.updatedAtMs, recordedAtMs),
		trainObservation: undefined,
	};
}

/**
 * The driver pressing "picked up" is the one witness who cannot be wrong about it.
 *
 * On 2026-10-07 David confirmed the pickup at 16:28 while the readout still had Eben on the train,
 * and it stayed there for another half hour because the rider's own phone never latched the
 * arrival. Only on the ride home, only today, and never backwards.
 */
export function advancePickedUpStatus(status: DriverFollowStatus, completedAtMs: number | null, nowMs: number, timezone = "America/Los_Angeles"): DriverFollowStatus {
	if (completedAtMs == null ||
		status.direction !== "home" ||
		!isBefore(status.phase, "at_destination") ||
		serviceDateKey(completedAtMs, timezone) !== serviceDateKey(nowMs, timezone)) {
		return status;
	}
	return {
		...status,
		phase: "at_destination",
		evidence: "declared",
		updatedAtMs: Math.max(status.updatedAtMs, completedAtMs),
		trainObservation: undefined,
		nextStopName: null,
		arriving: false,
	};
}

/** The parts of the shared pickup that know something the follow reading may not. */
export type PickupWitness = Pick<PickupState, "riderPhase" | "riderSeenAtMs" | "riderArrivedAtMs" | "riderArrivalConfidence" | "riderNextStopName" | "stage">;

const ARRIVAL_EVIDENCE: Record<PickupArrivalConfidence, FollowEvidence> = { observed: "observed", declared: "declared", estimated: "inferred" };

/**
 * The ride home as the pickup record has it, folded into the follow reading.
 *
 * The pickup card's step pills read the pickup row, and they were the more accurate of the two:
 * the row keeps his arrival claim once made, takes its next stop from BART realtime on the cron
 * while his phone is in a pocket, and knows the school fence. The monitor is now the only readout,
 * so it takes whichever is further along — the record never moves him backwards.
 */
export function reconcileFollowWithPickup(status: DriverFollowStatus | null, pickup: PickupWitness | null): DriverFollowStatus | null {
	if (!status || !pickup || (status.direction != null && status.direction !== "home")) {
		return status;
	}
	let next = status;
	const arrived = pickup.riderArrivedAtMs != null || pickup.riderPhase === "at_destination" || pickup.riderPhase === "arrived";
	if (arrived && isBefore(next.phase, "at_destination")) {
		next = {
			...next,
			phase: "at_destination",
			evidence: pickup.riderArrivalConfidence ? ARRIVAL_EVIDENCE[pickup.riderArrivalConfidence] : next.evidence,
			updatedAtMs: Math.max(next.updatedAtMs, pickup.riderArrivedAtMs ?? pickup.riderSeenAtMs ?? 0),
			trainObservation: undefined,
			nextStopName: null,
			nextStopId: null,
			arriving: false,
		};
	} else if (
		pickup.riderPhase != null &&
		pickup.riderSeenAtMs != null &&
		pickup.riderSeenAtMs >= next.updatedAtMs &&
		isBefore(next.phase, pickup.riderPhase) &&
		isBefore(pickup.riderPhase, "at_destination")
	) {
		next = { ...next, phase: pickup.riderPhase, updatedAtMs: pickup.riderSeenAtMs };
	}
	if (!next.leftStart && pickup.stage !== "stirring" && isBefore(next.phase, "at_station")) {
		next = { ...next, leftStart: true };
	}
	if (next.phase === "on_train" && next.nextStopName == null && pickup.riderNextStopName != null) {
		next = { ...next, nextStopName: pickup.riderNextStopName };
	}
	return next;
}

export function observeDriverTrainStatus(status: DriverFollowStatus, rawFix: unknown, recordedAtMs: number, nowMs: number, timezone = "America/Los_Angeles"): DriverFollowStatus {
	const position = mapFix(rawFix, recordedAtMs, 30_000);
	if (status.phase !== "on_train" || !status.planId || !position || position.source === "manual-test" ||
		position.accuracyMeters > 50 || status.boardingMs == null || status.alighting == null ||
		position.fixAtMs < status.boardingMs || serviceDateKey(status.boardingMs, timezone) !== serviceDateKey(nowMs, timezone) ||
		position.fixAtMs > nowMs || recordedAtMs > nowMs) return status;
	const boardingIndex = YELLOW_LINE_STATIONS.indexOf(status.boardingStationId as (typeof YELLOW_LINE_STATIONS)[number]);
	const destinationIndex = YELLOW_LINE_STATIONS.findIndex(id => id === status.alighting?.stopId);
	const macArthurIndex = YELLOW_LINE_STATIONS.indexOf("MCAR");
	const balboaParkIndex = YELLOW_LINE_STATIONS.indexOf("BALB");
	const passesMacArthur = status.route === "Yellow-N"
		? boardingIndex > macArthurIndex && destinationIndex >= 0 && destinationIndex < macArthurIndex
		: status.route === "Yellow-S" && boardingIndex >= 0 && boardingIndex < macArthurIndex && destinationIndex > macArthurIndex;
	// SFO is the one destination past the far end of the SF subway: Balboa Park to Daly City
	// surfaces again before the airport tunnel takes the signal back, so this is a second
	// checkpoint rather than a rename of the first — the schedule interpolation still carries the
	// dot through everything on both sides of it, and estimatedFollowMapPosition lets go of a
	// checkpoint the moment fixes stop, so trusting this one costs nothing if it never resolves.
	const passesBalboaPark = status.route === "Yellow-S" && boardingIndex >= 0 && boardingIndex < balboaParkIndex && destinationIndex > balboaParkIndex;
	const checkpoint = passesMacArthur && distanceMeters(position, BART_STATIONS.MCAR) + position.accuracyMeters <= 300
		? { name: "MacArthur" }
		: passesBalboaPark && distanceMeters(position, BART_STATIONS.BALB) + position.accuracyMeters <= 300
			? { name: "Balboa Park" }
			: null;
	if (!checkpoint) return status;
	return { ...status, evidence: "observed", trainObservation: { stationName: checkpoint.name, atMs: position.fixAtMs } };
}

/** Advance location from a matching station passage without changing phase provenance or age. */
export function advanceDriverTrainStatus(status: DriverFollowStatus, passedStationId: string): DriverFollowStatus {
	if (status.phase !== "on_train") {
		return status;
	}
	const passedIndex = YELLOW_LINE_STATIONS.indexOf(passedStationId as (typeof YELLOW_LINE_STATIONS)[number]);
	const step = status.route?.endsWith("-S") ? 1 : status.route?.endsWith("-N") ? -1 : 0;
	const nextId = passedIndex < 0 || step === 0 ? null : YELLOW_LINE_STATIONS[passedIndex + step] ?? null;
	if (!nextId) {
		return status;
	}
	const candidateIndex = YELLOW_LINE_STATIONS.indexOf(nextId);
	const destinationIndex = status.alighting
		? YELLOW_LINE_STATIONS.indexOf(status.alighting.stopId as (typeof YELLOW_LINE_STATIONS)[number])
		: -1;
	const candidateStopsRemaining = destinationIndex >= 0 ? Math.abs(destinationIndex - candidateIndex) + 1 : null;
	if (candidateStopsRemaining != null && status.stopsRemaining != null && status.stopsRemaining <= candidateStopsRemaining) {
		return status;
	}
	return {
		...status,
		passedStopId: passedStationId,
		nextStopName: BART_STATIONS[nextId]?.name ?? status.nextStopName,
		nextStopId: nextId,
		arriving: false,
		stopsRemaining: candidateStopsRemaining ?? status.stopsRemaining,
	};
}

/** Whether this reading is news, rather than the same place said again. */
export function isNewsToFollower(reading: FollowReading, mark: FollowMark, today: string): boolean {
	const step = stepOf(reading);
	return step != null && !(mark.date === today && mark.direction === reading.direction && mark.step === step);
}

/**
 * How long a step stands before the journey is allowed to be told it went backwards.
 *
 * Long enough to cover someone thumbing the progress line, short enough that a rider who really
 * did step back off a train is reported while it still matters.
 */
export const FOLLOW_SETTLE_MS = 3 * 60_000;

function isBehind(step: FollowStep, previous: string | null): boolean {
	const from = FOLLOW_STEPS.indexOf(previous as FollowStep);
	return from >= 0 && FOLLOW_STEPS.indexOf(step) < from;
}

/**
 * Whether to actually buzz the follower.
 *
 * The state machine is right to record every transition; a phone in someone's pocket is not right
 * to repeat them. On 2026-08-27 Eben's arrival at Rockridge was auto-detected and then stepped
 * back and forward again by hand within eight seconds, and each move was news by itself — three
 * notifications, the middle one naming a train nobody had boarded.
 *
 * So the journey may run forwards freely, and may only be taken backwards once the step it is
 * leaving has had time to be true. A suppressed reading leaves the mark alone, so the correction
 * still lands if it turns out to be real.
 */
export function shouldTellFollower(reading: FollowReading, mark: FollowMark, today: string, nowMs: number): boolean {
	const step = stepOf(reading);
	if (step == null || !isNewsToFollower(reading, mark, today)) {
		return false;
	}
	// A different day or a different leg is a different story, so nothing about it is a reversal.
	if (mark.date !== today || mark.direction !== reading.direction || !isBehind(step, mark.step)) {
		return true;
	}
	return mark.sentAtMs == null || nowMs - mark.sentAtMs >= FOLLOW_SETTLE_MS;
}

export interface FollowMessage {
	title: string;
	body: string;
	tag: string;
}

/**
 * The message itself, as a sentence about him rather than a label for a phase.
 *
 * Each leg says the one extra thing the follower is waiting on. Going out that is when he reaches
 * school, because the train is only the middle of it; coming home it is the platform he steps off
 * at, because that is where someone is meeting him.
 */
export function followMessage(name: string, reading: FollowReading, timezone: string, nowMs: number): FollowMessage {
	const at = (ms: number) => formatClockWithMeridiem(ms, timezone);
	const homeward = reading.direction === "home";
	const step = stepOf(reading);
	const tag = `follow-${reading.direction ?? "trip"}-${step ?? reading.phase}`;
	const say = (title: string, body: string): FollowMessage => ({ title: `${name} ${title}`, body, tag });

	// The moment it fired is the arrival. The plan cannot be trusted for it: by the time he is off,
	// the deck has moved on to the next service, whose times are about a train he never rode.
	if (step === "off") {
		const where = reading.alighting ? stationLabel(reading.alighting.stopName) : null;
		return say("is off the train", where ? `${where} ${at(nowMs)}` : at(nowMs));
	}
	if (step === "arrived") {
		return say("is at school", reading.finalLabel ? `${reading.finalLabel} ${at(nowMs)}` : at(nowMs));
	}
	/*
	 * No train times on these two.
	 *
	 * Neither is a decision anyone acts on: he is at school with the app open, or he is walking down
	 * the hill. Trains run every ten minutes and the follower will open the app if he wants one, so
	 * a departure here is a number to read past on the way to the only word that matters.
	 */
	if (step === "start") {
		return say("is at school", "He has the app open.");
	}
	if (step === "leaving") {
		return say("has left school", "On his way to the station.");
	}

	const line = trainLine(reading, timezone, step === "train");
	if (step === "train") {
		// Where the train leaves him is the end of it on the way home; going out there is a shuttle
		// and a walk after it, and school is the time that actually matters.
		const school = !homeward && reading.finalLabel && reading.finalMs != null ? `${reading.finalLabel} ${at(reading.finalMs)}` : null;
		const body = [line, school].filter(Boolean).join(" · ");
		return say(homeward ? "is on the train" : "is on a train", body || "No train picked yet.");
	}
	// At the station and on the platform are ninety seconds apart and mean the same thing to
	// someone at the other end, so only the first arrives — but it should say which one it was.
	return say(reading.phase === "at_station" ? "is at the station" : "is on the platform", line ?? "No train picked yet.");
}

/** The ride itself: where it leaves from, when, where it puts him, and on which line. */
function trainLine(reading: FollowReading, timezone: string, aboard: boolean): string | null {
	const at = (ms: number) => formatClockWithMeridiem(ms, timezone);
	const parts: string[] = [];
	if (reading.boardingMs != null && reading.boardingName) {
		const leg =
			reading.alighting != null
				? `${stationLabel(reading.boardingName)} ${at(reading.boardingMs)} → ${stationLabel(reading.alighting.stopName)} ${at(reading.alighting.predictedMs)}`
				: `${stationLabel(reading.boardingName)} ${at(reading.boardingMs)}`;
		parts.push(aboard ? leg : `Next train ${leg}`);
	}
	if (reading.route) {
		parts.push(reading.headsign ? `${reading.route} to ${headsignLabel(reading.headsign)}` : reading.route);
	}
	return parts.length > 0 ? parts.join(" · ") : null;
}
